import { useState, useEffect, useMemo } from "react";
import { api, formatCurrency, formatPercent, monthNames } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  TrendingUp,
  TrendingDown,
  Package,
  Clock,
  CheckCircle,
  Target,
  DollarSign,
  Loader2,
  CalendarDays,
  AlertTriangle,
  Car,
  Mail,
  Wallet,
  ClipboardList,
  FileDown,
  HardHat,
  Wrench,
  CheckSquare,
  Settings2
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { generateManagementReportPdf } from "@/lib/managementReportPdf";
import { Button } from "@/components/ui/button";
import { PaymentReminderDialog } from "@/components/PaymentReminderDialog";
import { SellerMonthlyProgressCard } from "@/components/SellerMonthlyProgressCard";
import TopOrdersCard from "@/components/dashboard/TopOrdersCard";
import { DashboardCustomizer } from "@/components/dashboard/DashboardCustomizer";
import DashboardCompareWidgets from "@/components/analytics/DashboardCompareWidgets";
import SubcontractorComplianceAlerts from "@/components/dashboard/SubcontractorComplianceAlerts";
import LogisticsItemsWidget from "@/components/dashboard/LogisticsItemsWidget";
import MaterialsPendingLockWidget from "@/components/dashboard/MaterialsPendingLockWidget";

const DASHBOARD_TABLE_KEY = "dashboard_sections";

// Bento sizes use a 12-column grid on lg+. Mobile collapses to 1 column.
// Pairs (6/6 or 8/4) sit side-by-side; full rows span 12.
const SECTION_DEFINITIONS = [
  { key: "missing-order-date",  label: "Bandeau : commandes sans date",     bento: "lg:col-span-12" },
  { key: "pending-validations", label: "Validations en attente",            bento: "lg:col-span-12" },
  { key: "kpis",                label: "Indicateurs clés (5 cartes)",       bento: "lg:col-span-12" },
  { key: "seller-progress",     label: "Objectifs vendeurs",                bento: "lg:col-span-6" },
  { key: "outstanding",         label: "Encours client",                    bento: "lg:col-span-12" },
  { key: "admin-tasks",         label: "Administratif (tâches)",            bento: "lg:col-span-6" },
  { key: "contracts",           label: "Contrats à renouveler",             bento: "lg:col-span-6" },
  { key: "late-orders",         label: "Commandes en retard",               bento: "lg:col-span-6" },
  { key: "price-alerts",        label: "Alertes de prix fournisseurs",      bento: "lg:col-span-6" },
  { key: "equipment-alerts",    label: "Alertes véhicules",                 bento: "lg:col-span-6" },
  { key: "stock-forecast",      label: "Prévision de stock",                bento: "lg:col-span-12" },
  { key: "progress",            label: "Objectif mensuel (barre)",          bento: "lg:col-span-4" },
  { key: "summary",             label: "Résumé + indicateurs clés",         bento: "lg:col-span-8" },
  { key: "top-orders",          label: "Top / Flop commandes",              bento: "lg:col-span-12" },
  { key: "advanced-compare",    label: "Comparaisons avancées (multi-vues)",   bento: "lg:col-span-12" },
  { key: "subcontractor-compliance", label: "Alertes conformité sous-traitants", bento: "lg:col-span-6" },
  { key: "logistics-items", label: "Éléments logistiques (vue globale)", bento: "lg:col-span-6" },
  { key: "materials-pending-lock", label: "Matières à verrouiller", bento: "lg:col-span-6" },
];

const bentoOf = (key) =>
  SECTION_DEFINITIONS.find((s) => s.key === key)?.bento || "lg:col-span-12";

export const DashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [stockForecast, setStockForecast] = useState(null);
  const [lateOrders, setLateOrders] = useState(null);
  const [adminTasksSummary, setAdminTasksSummary] = useState(null);
  const [equipmentAlerts, setEquipmentAlerts] = useState(null);
  const [outstanding, setOutstanding] = useState(null);
  const [contractsData, setContractsData] = useState(null);
  const [priceAlerts, setPriceAlerts] = useState(null);
  const [reminderOrder, setReminderOrder] = useState(null);
  const [missingOrderDateCount, setMissingOrderDateCount] = useState(0);
  const [pendingValidations, setPendingValidations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [customizerOpen, setCustomizerOpen] = useState(false);
  const [sectionOrder, setSectionOrder] = useState(SECTION_DEFINITIONS.map((s) => s.key));
  const [sectionVisibility, setSectionVisibility] = useState({});
  const [compareRefData, setCompareRefData] = useState({ orderTypes: [], sellersList: [] });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2015 + 1 }, (_, i) => currentYear - i);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const [statsRes, forecastRes, lateRes, eqAlertRes, outstandingRes, adminTasksRes, contractsRes, priceAlertsRes, missingDateRes, pendingRes] = await Promise.all([
        api.getDashboardStats({ year: selectedYear, month: selectedMonth }),
        api.getStockForecast(),
        api.getLateOrders(),
        api.getEquipmentAlerts(),
        api.getOutstandingReceivables().catch(() => ({ data: null })),
        api.getAdminTasksDashboardSummary().catch(() => ({ data: null })),
        api.getContracts().catch(() => ({ data: null })),
        api.getPriceAlerts(15).catch(() => ({ data: null })),
        api.getMissingOrderDateCount().catch(() => ({ data: { count: 0 } })),
        api.getDashboardPendingValidations().catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setStockForecast(forecastRes.data);
      setLateOrders(lateRes.data);
      setEquipmentAlerts(eqAlertRes.data);
      setOutstanding(outstandingRes.data);
      setAdminTasksSummary(adminTasksRes.data);
      setContractsData(contractsRes.data);
      setPriceAlerts(priceAlertsRes.data);
      setMissingOrderDateCount(missingDateRes?.data?.count || 0);
      setPendingValidations(pendingRes?.data || null);
    } catch (error) {
      toast.error("Erreur lors du chargement des statistiques");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [selectedYear, selectedMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load per-user dashboard layout preferences
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.getUserTablePref(DASHBOARD_TABLE_KEY);
        if (data?.order && data.order.length) setSectionOrder(data.order);
        if (data?.visibility) setSectionVisibility(data.visibility);
      } catch (_e) {
        // ignore — defaults already set
      }
    })();
  }, []);

  // Load reference data for the advanced-compare widget
  useEffect(() => {
    (async () => {
      try {
        const [otRes, slRes] = await Promise.all([
          api.getOrderTypes().catch(() => ({ data: [] })),
          api.getAnalyticsSellersList().catch(() => ({ data: [] })),
        ]);
        setCompareRefData({ orderTypes: otRes.data || [], sellersList: slRes.data || [] });
      } catch (_e) { /* ignore */ }
    })();
  }, []);

  const saveLayout = async (newOrder, newVis) => {
    try {
      await api.upsertUserTablePref(DASHBOARD_TABLE_KEY, { order: newOrder, visibility: newVis });
      setSectionOrder(newOrder);
      setSectionVisibility(newVis);
      toast.success("Mise en page enregistrée");
    } catch (_e) {
      toast.error("Échec de l'enregistrement");
    }
  };

  const resetLayout = async () => {
    try {
      await api.deleteUserTablePref(DASHBOARD_TABLE_KEY);
      setSectionOrder(SECTION_DEFINITIONS.map((s) => s.key));
      setSectionVisibility({});
      toast.success("Mise en page réinitialisée");
    } catch (_e) {
      toast.error("Échec de la réinitialisation");
    }
  };

  const handleExportReport = async () => {
    try {
      toast.info("Génération du reporting…");
      const [marginRes, forecastRes] = await Promise.all([
        api.getMaterialMargins().catch(() => ({ data: null })),
        api.getSupplierStockForecast().catch(() => ({ data: null })),
      ]);
      generateManagementReportPdf({
        year: selectedYear,
        month: selectedMonth,
        stats,
        marginSummary: marginRes.data?.summary,
        outstanding,
        lateOrders,
        adminTasks: adminTasksSummary,
        stockForecast: forecastRes.data,
      });
      toast.success("Reporting PDF téléchargé");
    } catch (_e) {
      toast.error("Erreur lors de la génération du PDF");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const visibleOrder = sectionOrder.filter((k) => sectionVisibility[k] !== false);
  const orderOf = (k) => {
    const i = visibleOrder.indexOf(k);
    return i === -1 ? 999 : i;
  };
  const isVisible = (k) => sectionVisibility[k] !== false;
  const sectionStyle = (k) => ({ order: orderOf(k) });

  return (
    <div className="space-y-8 animate-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">
            Tableau de bord
          </h1>
          <p className="text-muted-foreground mt-1">
            Vue d'ensemble de votre activité
          </p>
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" onClick={() => setCustomizerOpen(true)} data-testid="customize-dashboard-btn">
            <Settings2 className="h-4 w-4 mr-1" /> Personnaliser
          </Button>
          <Button variant="outline" onClick={handleExportReport} data-testid="export-management-report">
            <FileDown className="h-4 w-4 mr-1" /> Reporting PDF
          </Button>
          <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
            <SelectTrigger className="w-40" data-testid="month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthNames.map((name, idx) => (
                <SelectItem key={idx} value={String(idx + 1)}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-28" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((year) => (
                <SelectItem key={year} value={String(year)}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Customizable sections (per-user order/visibility) — Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6 grid-flow-row-dense auto-rows-min">

      {/* Banner : commandes sans date de commande */}
      {missingOrderDateCount > 0 && (
        <div className={`${bentoOf('missing-order-date')} min-w-0`} style={sectionStyle('missing-order-date')} hidden={!isVisible('missing-order-date')} data-testid="bento-missing-order-date">
        <div
          className="flex items-center justify-between gap-4 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 transition-all duration-200 ease-out hover:shadow-[0_4px_12px_-2px_rgba(217,119,6,0.12)]"
          data-testid="missing-order-date-banner"
        >
          <div className="flex items-center gap-3">
            <CalendarDays className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <div>
              <div className="font-medium text-amber-900">
                {missingOrderDateCount} commande{missingOrderDateCount > 1 ? "s" : ""} sans date de commande
              </div>
              <div className="text-xs text-amber-800">
                Complétez ces commandes pour améliorer vos rapports et analyses.
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="border-amber-300 text-amber-900 hover:bg-amber-100"
            onClick={() => navigate("/commandes?missing_order_date=true")}
            data-testid="missing-order-date-cta"
          >
            Compléter
          </Button>
        </div>
        </div>
      )}

      {/* Banner : validations en attente (Fiches salariés + Demandes chantier) */}
      {pendingValidations && pendingValidations.total_pending > 0 && (
        <div className={`${bentoOf('pending-validations')} min-w-0`} style={sectionStyle('pending-validations')} hidden={!isVisible('pending-validations')} data-testid="bento-pending-validations">
        <Card className="border-2 border-indigo-200 bg-indigo-50/70" data-testid="pending-validations-card">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 bg-indigo-100 rounded-sm flex items-center justify-center shrink-0">
                <CheckSquare className="h-6 w-6 text-indigo-600" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="text-base font-heading font-bold text-indigo-900">
                    {pendingValidations.total_pending} validation(s) en attente
                  </p>
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => navigate("/fiches-salaries")}
                    className="text-left p-3 rounded-xl bg-white border border-indigo-100 hover:border-indigo-300 hover:shadow-sm transition-all duration-200 ease-out"
                    data-testid="pending-card-hours"
                  >
                    <div className="flex items-center gap-2 text-indigo-700">
                      <Clock className="h-4 w-4" />
                      <span className="text-xs font-medium">Heures à valider</span>
                    </div>
                    <div className="text-2xl font-bold text-indigo-900 mt-1">
                      {pendingValidations.submissions?.pending_hours || 0}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/fiches-salaries")}
                    className="text-left p-3 rounded-xl bg-white border border-indigo-100 hover:border-indigo-300 hover:shadow-sm transition-all duration-200 ease-out"
                    data-testid="pending-card-absences"
                  >
                    <div className="flex items-center gap-2 text-indigo-700">
                      <ClipboardList className="h-4 w-4" />
                      <span className="text-xs font-medium">Absences à valider</span>
                    </div>
                    <div className="text-2xl font-bold text-indigo-900 mt-1">
                      {pendingValidations.submissions?.pending_absences || 0}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/demandes-chantier")}
                    className="text-left p-3 rounded-xl bg-white border border-indigo-100 hover:border-indigo-300 hover:shadow-sm transition-all duration-200 ease-out"
                    data-testid="pending-card-field-requests"
                  >
                    <div className="flex items-center gap-2 text-indigo-700">
                      <Wrench className="h-4 w-4" />
                      <span className="text-xs font-medium">Demandes chantier</span>
                    </div>
                    <div className="text-2xl font-bold text-indigo-900 mt-1">
                      {pendingValidations.field_requests?.pending || 0}
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>
      )}

      {/* KPI Cards */}
      <div className={`${bentoOf('kpis')} min-w-0`} style={sectionStyle('kpis')} hidden={!isVisible('kpis')} data-testid="bento-kpis">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {/* CA Réalisé */}
        <Card
          className="kpi-card card-hover cursor-pointer"
          data-testid="kpi-realized-ca"
          onClick={() => navigate(`/commandes?progress_year=${selectedYear}&progress_month=${selectedMonth}`)}
          title="Voir les commandes avec un avancement saisi ce mois-ci"
        >
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  CA réalisé du mois
                </p>
                <p className="kpi-value text-primary currency mt-2">
                  {formatCurrency(stats?.realized_ca || 0)}
                </p>
              </div>
              <div className="h-12 w-12 bg-primary/10 rounded-sm flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-primary" strokeWidth={1.5} />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">
                Commandes: {formatCurrency(stats?.total_ca || 0)}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Pending Orders */}
        <Card
          className="kpi-card card-hover cursor-pointer"
          data-testid="kpi-pending"
          onClick={() => navigate("/commandes?status=pending,in_progress")}
          title="Voir toutes les commandes non terminées"
        >
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Stock en attente
                </p>
                <p className="kpi-value text-yellow-600 currency mt-2">
                  {formatCurrency(stats?.pending_ca || 0)}
                </p>
              </div>
              <div className="h-12 w-12 bg-yellow-100 rounded-sm flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-600" strokeWidth={1.5} />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">
                {stats?.pending_orders || 0} commande(s) non réalisée(s)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Argent dehors */}
        <Card
          className={`kpi-card card-hover ${stats?.money_out_count > 0 ? "border-red-300 cursor-pointer" : ""}`}
          data-testid="kpi-money-out"
          onClick={() => stats?.money_out_count > 0 && navigate("/commandes?filter=money_out")}
          title={stats?.money_out_count > 0 ? "Voir les commandes concernées par l'argent dehors" : ""}
        >
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Argent dehors
                </p>
                <p className={`kpi-value currency mt-2 ${stats?.money_out_count > 0 ? "text-red-600" : "text-slate-500"}`}>
                  {formatCurrency(stats?.money_out_total || 0)}
                </p>
              </div>
              <div className={`h-12 w-12 rounded-sm flex items-center justify-center ${stats?.money_out_count > 0 ? "bg-red-100" : "bg-slate-100"}`}>
                <Wallet className={`h-6 w-6 ${stats?.money_out_count > 0 ? "text-red-600" : "text-slate-500"}`} strokeWidth={1.5} />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">
                {stats?.money_out_count || 0} commande(s) sans acompte ou à solder
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Commandes du mois */}
        <Card className="kpi-card card-hover" data-testid="kpi-monthly-orders">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Commandes du mois
                </p>
                <p className="kpi-value text-secondary mt-2">
                  {stats?.total_orders || 0}
                </p>
              </div>
              <div className="h-12 w-12 bg-secondary/10 rounded-sm flex items-center justify-center">
                <ClipboardList className="h-6 w-6 text-secondary" strokeWidth={1.5} />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">
                Montant total HT :{" "}
                <span className="font-semibold text-indigo-700">
                  {formatCurrency(stats?.total_ca || 0)}
                </span>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Dépassement horaire */}
        <Card
          className={`kpi-card card-hover ${stats?.overrun_count > 0 ? "border-red-300 cursor-pointer" : ""}`}
          data-testid="kpi-overrun"
          onClick={() => stats?.overrun_count > 0 && navigate("/commandes")}
          title={stats?.overrun_count > 0 ? "Voir les commandes en dépassement horaire" : ""}
        >
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Dépassement horaire
                </p>
                <p className={`kpi-value mt-2 ${stats?.overrun_count > 0 ? "text-red-600" : "text-slate-500"}`}>
                  {stats?.overrun_count || 0}
                </p>
              </div>
              <div className={`h-12 w-12 rounded-sm flex items-center justify-center ${stats?.overrun_count > 0 ? "bg-red-100" : "bg-slate-100"}`}>
                <HardHat className={`h-6 w-6 ${stats?.overrun_count > 0 ? "text-red-600" : "text-slate-500"}`} strokeWidth={1.5} />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">
                {stats?.overrun_count > 0
                  ? <>+{stats.overrun_total_hours} h au-delà du prévisionnel</>
                  : "Aucun chantier en dépassement"}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
      </div>

      {/* Seller monthly objective progress */}
      <div className={`${bentoOf('seller-progress')} min-w-0`} style={sectionStyle('seller-progress')} hidden={!isVisible('seller-progress')} data-testid="bento-seller-progress">
        <SellerMonthlyProgressCard year={selectedYear} month={selectedMonth} />
      </div>

      {/* Outstanding Receivables KPI + overdue alert */}
      {outstanding && outstanding.total_orders > 0 && (
        <div className={`${bentoOf('outstanding')} min-w-0`} style={sectionStyle('outstanding')} hidden={!isVisible('outstanding')} data-testid="bento-outstanding">
        <Card
          className={`card-hover ${outstanding.overdue_count > 0 ? "border-2 border-amber-200 bg-amber-50/40" : ""}`}
          data-testid="kpi-outstanding"
        >
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 bg-amber-100 rounded-sm flex items-center justify-center shrink-0">
                  <Wallet className="h-6 w-6 text-amber-700" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Encours client (TTC)</p>
                  <p className="kpi-value currency mt-2 text-amber-700">
                    {formatCurrency(outstanding.total_outstanding)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    sur {outstanding.total_orders} commande(s) non soldée(s)
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-xs lg:col-span-2">
                <div className="p-3 rounded-sm border bg-background">
                  <p className="text-muted-foreground">En attente</p>
                  <p className="currency font-semibold mt-1">
                    {formatCurrency(outstanding.by_status?.pending || 0)}
                  </p>
                </div>
                <div className="p-3 rounded-sm border bg-background">
                  <p className="text-muted-foreground">En cours</p>
                  <p className="currency font-semibold mt-1">
                    {formatCurrency(outstanding.by_status?.in_progress || 0)}
                  </p>
                </div>
                <div className="p-3 rounded-sm border bg-background">
                  <p className="text-muted-foreground">Terminées non payées</p>
                  <p className="currency font-semibold mt-1">
                    {formatCurrency(outstanding.by_status?.completed || 0)}
                  </p>
                </div>
              </div>
            </div>

            {outstanding.overdue_count > 0 && (
              <div className="mt-4 pt-4 border-t space-y-2" data-testid="outstanding-overdue-block">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <p className="text-sm font-medium text-amber-800">
                    À relancer : {formatCurrency(outstanding.overdue_outstanding)} sur {outstanding.overdue_count} commande(s) terminée(s) depuis plus de 30 jours
                  </p>
                </div>
                <div className="space-y-1">
                  {outstanding.top_overdue.map((o) => (
                    <div
                      key={o.order_id}
                      className="flex items-center justify-between text-xs p-2 rounded-sm bg-background border"
                      data-testid={`outstanding-overdue-${o.order_id}`}
                    >
                      <span className="font-mono">{o.reference}</span>
                      <span className="flex-1 px-2 truncate">{o.client_name}</span>
                      <span className="font-medium currency">{formatCurrency(o.remaining)}</span>
                      <span className="ml-3 text-amber-700">+{o.days_since_completion}j</span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="ml-3 h-7 px-2"
                        onClick={() => setReminderOrder(o)}
                        data-testid={`remind-btn-${o.order_id}`}
                      >
                        <Mail className="h-3 w-3 mr-1" /> Relancer
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        </div>
      )}

      {/* Admin Tasks Summary */}
      {adminTasksSummary && (adminTasksSummary.overdue_count > 0 || adminTasksSummary.this_week_count > 0 || adminTasksSummary.high_backlog_count > 0) && (
        <div className={`${bentoOf('admin-tasks')} min-w-0`} style={sectionStyle('admin-tasks')} hidden={!isVisible('admin-tasks')} data-testid="bento-admin-tasks">
        <Card
          className={`border-2 cursor-pointer hover:shadow-[0_8px_24px_-4px_rgba(24,24,27,0.08)] transition-all duration-200 ease-out h-full ${
            adminTasksSummary.overdue_count > 0 ? "border-red-200 bg-red-50/60" : "border-amber-200 bg-amber-50/60"
          }`}
          data-testid="admin-tasks-summary-card"
          onClick={() => navigate("/administratif")}
        >
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className={`h-12 w-12 rounded-sm flex items-center justify-center shrink-0 ${
                adminTasksSummary.overdue_count > 0 ? "bg-destructive/10" : "bg-amber-100"
              }`}>
                <ClipboardList className={`h-6 w-6 ${
                  adminTasksSummary.overdue_count > 0 ? "text-destructive" : "text-amber-600"
                }`} strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <p className={`text-base font-heading font-bold ${
                    adminTasksSummary.overdue_count > 0 ? "text-destructive" : "text-amber-800"
                  }`}>
                    Administratif
                  </p>
                  {adminTasksSummary.overdue_count > 0 && (
                    <span className="text-sm font-semibold text-red-700">{adminTasksSummary.overdue_count} en retard</span>
                  )}
                  {adminTasksSummary.this_week_count > 0 && (
                    <span className="text-sm font-medium text-amber-800">{adminTasksSummary.this_week_count} cette semaine</span>
                  )}
                  {adminTasksSummary.high_backlog_count > 0 && (
                    <span className="text-sm font-medium text-slate-700">{adminTasksSummary.high_backlog_count} priorité haute (backlog)</span>
                  )}
                </div>
                <div className="mt-3 space-y-1">
                  {adminTasksSummary.overdue.slice(0, 3).map((o, idx) => (
                    <div key={`od-${idx}`} className="flex items-center gap-2 text-sm">
                      <AlertTriangle className="h-3 w-3 text-red-600 shrink-0" />
                      <span className="font-medium truncate">{o.title}</span>
                      <span className="text-xs text-red-700 whitespace-nowrap ml-auto">échéance {new Date(o.due_date).toLocaleDateString("fr-FR")}</span>
                    </div>
                  ))}
                  {adminTasksSummary.this_week.slice(0, 3).map((o, idx) => (
                    <div key={`tw-${idx}`} className="flex items-center gap-2 text-sm">
                      <Clock className="h-3 w-3 text-amber-600 shrink-0" />
                      <span className="truncate">{o.title}</span>
                      <span className="text-xs text-muted-foreground whitespace-nowrap ml-auto">{new Date(o.due_date).toLocaleDateString("fr-FR")}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>
      )}

      {/* Contracts alerts card */}
      {contractsData && contractsData.alerts_count > 0 && (() => {
        const alertsList = (contractsData.items || []).filter((c) => c.alert_active)
          .sort((a, b) => (a.days_until_end ?? 999) - (b.days_until_end ?? 999));
        return (
          <div className={`${bentoOf('contracts')} min-w-0`} style={sectionStyle('contracts')} hidden={!isVisible('contracts')} data-testid="bento-contracts">
          <Card
            className="border-2 border-amber-200 bg-amber-50/60 cursor-pointer hover:shadow-[0_8px_24px_-4px_rgba(24,24,27,0.08)] transition-all duration-200 ease-out h-full"
            data-testid="contracts-alert-card"
            onClick={() => navigate("/administratif?tab=contracts")}
          >
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 bg-amber-100 rounded-sm flex items-center justify-center shrink-0">
                  <FileDown className="h-6 w-6 text-amber-600" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="text-base font-heading font-bold text-amber-800">
                      Contrats à renouveler
                    </p>
                    <span className="text-sm font-semibold text-amber-800">
                      {contractsData.alerts_count} en préavis
                    </span>
                    <span className="text-xs text-muted-foreground">
                      (cliquez pour ouvrir l'onglet administratif)
                    </span>
                  </div>
                  <div className="mt-3 space-y-1">
                    {alertsList.slice(0, 5).map((c) => (
                      <div key={c.id} className="flex items-center gap-2 text-sm">
                        <AlertTriangle className="h-3 w-3 text-amber-600 shrink-0" />
                        <span className="font-medium truncate">{c.title}</span>
                        {c.supplier && (
                          <span className="text-xs text-muted-foreground whitespace-nowrap">({c.supplier})</span>
                        )}
                        <span className="text-xs text-amber-900 whitespace-nowrap ml-auto">
                          {c.days_until_end >= 0
                            ? `fin dans ${c.days_until_end} j`
                            : `fin il y a ${-c.days_until_end} j`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        );
      })()}

      {/* Late Orders Alert */}
      {lateOrders && lateOrders.count > 0 && (
        <div className={`${bentoOf('late-orders')} min-w-0`} style={sectionStyle('late-orders')} hidden={!isVisible('late-orders')} data-testid="bento-late-orders">
        <Card className="border-2 border-red-200 bg-red-50/60 h-full" data-testid="late-orders-alert">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 bg-destructive/10 rounded-sm flex items-center justify-center shrink-0">
                <AlertTriangle className="h-6 w-6 text-destructive" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-heading font-bold text-destructive">
                  {lateOrders.count} commande(s) en retard
                </p>
                <div className="mt-3 space-y-2">
                  {lateOrders.late_orders.slice(0, 5).map((o) => (
                    <div key={o.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 py-2 border-b border-red-200 last:border-0">
                      <div className="min-w-0">
                        <span className="font-mono text-sm font-medium">{o.reference}</span>
                        <span className="text-sm text-muted-foreground ml-2">{o.client_name}</span>
                      </div>
                      <span className="text-sm font-bold text-destructive whitespace-nowrap">
                        {o.days_late > 0 ? `${o.days_late}j de retard` : `Dépasse la date limite de ${o.days_late_constraint}j`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>
      )}

      {/* Price Alerts (supplier analytics) */}
      {priceAlerts && priceAlerts.count > 0 && (
        <div className={`${bentoOf('price-alerts')} min-w-0`} style={sectionStyle('price-alerts')} hidden={!isVisible('price-alerts')} data-testid="bento-price-alerts">
        <Card className="border-2 border-red-200 bg-red-50/60 h-full" data-testid="price-alerts-dashboard">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 bg-red-100 rounded-sm flex items-center justify-center shrink-0">
                <TrendingUp className="h-6 w-6 text-red-600" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-base font-heading font-bold text-red-800">
                    {priceAlerts.count} hausse(s) de prix anormale(s) détectée(s)
                  </p>
                  <button
                    onClick={() => navigate("/fournisseurs?tab=analytics")}
                    className="text-xs text-red-700 underline-offset-4 hover:underline"
                    data-testid="view-all-price-alerts"
                  >
                    Tout voir →
                  </button>
                </div>
                <div className="mt-2 space-y-1">
                  {priceAlerts.alerts.slice(0, 3).map((a) => (
                    <div key={a.product_id} className="text-sm flex justify-between gap-2">
                      <span className="truncate">
                        <span className="font-medium">{a.product_name}</span>
                        <span className="text-muted-foreground"> ({a.supplier_name})</span>
                      </span>
                      <span className="font-bold text-red-700 shrink-0">
                        +{a.delta_pct}% ({a.previous_amount.toFixed(2)}€ → {a.current_amount.toFixed(2)}€)
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>
      )}

      {/* Equipment Alerts */}
      {equipmentAlerts && equipmentAlerts.total_count > 0 && (
        <div className={`${bentoOf('equipment-alerts')} min-w-0`} style={sectionStyle('equipment-alerts')} hidden={!isVisible('equipment-alerts')} data-testid="bento-equipment-alerts">
        <Card className="border-2 border-amber-200 bg-amber-50/60 h-full" data-testid="equipment-alerts-dashboard">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 bg-amber-100 rounded-sm flex items-center justify-center shrink-0">
                <Car className="h-6 w-6 text-amber-600" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-heading font-bold text-amber-800">
                  {equipmentAlerts.total_count} alerte(s) matériel
                </p>
                <div className="mt-2 space-y-1">
                  {equipmentAlerts.vehicle_alerts.slice(0, 3).map((a, i) => (
                    <div key={i} className="text-sm flex justify-between gap-2">
                      <span>{a.vehicle_name} <span className="font-mono text-xs">({a.registration})</span> - {a.inspection_type === "technique" ? "CT" : "Antipollution"}</span>
                      <span className={`font-bold ${a.days_remaining < 0 ? "text-destructive" : "text-amber-700"}`}>
                        {a.days_remaining < 0 ? `Expiré (${Math.abs(a.days_remaining)}j)` : `${a.days_remaining}j restants`}
                      </span>
                    </div>
                  ))}
                  {equipmentAlerts.epi_alerts.slice(0, 3).map((a, i) => (
                    <div key={`epi-${i}`} className="text-sm flex justify-between gap-2">
                      <span>EPI: {a.name}</span>
                      <span className={`font-bold ${a.days_remaining < 0 ? "text-destructive" : "text-amber-700"}`}>
                        {a.days_remaining < 0 ? `Expiré (${Math.abs(a.days_remaining)}j)` : `${a.days_remaining}j restants`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>
      )}

      {/* Stock Forecast Card */}
      {stockForecast && stockForecast.orders_count > 0 && (
        <div className={`${bentoOf('stock-forecast')} min-w-0`} style={sectionStyle('stock-forecast')} hidden={!isVisible('stock-forecast')} data-testid="bento-stock-forecast">
        <Card className="border-2 border-blue-100 bg-gradient-to-r from-blue-50/50 to-transparent" data-testid="stock-forecast-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-6">
              <div className="h-16 w-16 bg-primary/10 rounded-sm flex items-center justify-center shrink-0">
                <CalendarDays className="h-8 w-8 text-primary" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground">
                  Prévision de stock de commandes
                </p>
                <p className="text-2xl font-heading font-bold text-primary mt-1">
                  {stockForecast.forecast_end_date_formatted}
                </p>
                <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
                  <span>{stockForecast.orders_count} commande(s) en cours</span>
                  <span>•</span>
                  <span className="currency">{formatCurrency(stockForecast.total_stock_value)}</span>
                  <span>•</span>
                  <span>{stockForecast.total_stock_days} jours ouvrés</span>
                  {stockForecast.total_ouvriers > 0 && (
                    <>
                      <span>•</span>
                      <span>Équipe : {stockForecast.total_ouvriers} ouvrier(s)</span>
                    </>
                  )}
                </div>
                {stockForecast.absent_worker_days_in_period > 0 && (
                  <div className="flex items-center gap-2 mt-2 text-xs bg-amber-100 text-amber-900 px-2 py-1 rounded inline-flex" data-testid="absence-impact">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Impact absences : {stockForecast.absent_worker_days_in_period} jour(s)-homme perdus sur la période
                  </div>
                )}

                {/* Mini-jauge : Capacité interne vs Sous-traitance */}
                {stockForecast.total_stock_value > 0 && (() => {
                  const total = stockForecast.total_stock_value || 0;
                  const sub = stockForecast.subcontracted_stock_value || 0;
                  const internal = Math.max(0, total - sub);
                  const subPct = total > 0 ? Math.round((sub / total) * 100) : 0;
                  const internalPct = 100 - subPct;
                  if (sub <= 0) return null;
                  return (
                    <div className="mt-3 max-w-md" data-testid="capacity-gauge">
                      <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
                        <span className="font-medium text-foreground">Répartition du stock</span>
                        <span>
                          {stockForecast.subcontracted_orders_count} commande{stockForecast.subcontracted_orders_count > 1 ? "s" : ""} sous-traitée{stockForecast.subcontracted_orders_count > 1 ? "s" : ""}
                        </span>
                      </div>
                      <div className="flex h-2.5 rounded-full overflow-hidden bg-zinc-100 border border-zinc-200" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={subPct}>
                        <div
                          className="bg-primary transition-all"
                          style={{ width: `${internalPct}%` }}
                          title={`Interne : ${formatCurrency(internal)}`}
                          data-testid="capacity-gauge-internal"
                        />
                        <div
                          className="bg-amber-500 transition-all"
                          style={{ width: `${subPct}%` }}
                          title={`Sous-traitance : ${formatCurrency(sub)}`}
                          data-testid="capacity-gauge-sub"
                        />
                      </div>
                      <div className="flex items-center justify-between text-[11px] mt-1">
                        <span className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-primary inline-block" />
                          <span className="font-medium">Interne</span>
                          <span className="text-muted-foreground">{formatCurrency(internal)} · {internalPct}%</span>
                        </span>
                        <span className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
                          <span className="font-medium">Sous-traitance</span>
                          <span className="text-muted-foreground">{formatCurrency(sub)} · {subPct}%</span>
                        </span>
                      </div>
                      {!stockForecast.include_subcontracting_in_stock_days && (
                        <p className="text-[10px] text-muted-foreground mt-1.5 italic">
                          Les commandes sous-traitées sont exclues du calcul des jours (réglable dans Paramètres).
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              * Calcul basé sur les jours ouvrés (lundi-vendredi), hors jours fériés français. Prend en compte les absences maladie/CP/RTT/etc. pour ajuster la capacité quotidienne.
            </p>
          </CardContent>
        </Card>
        </div>
      )}

      {/* Progress Card */}
      <div className={`${bentoOf('progress')} min-w-0`} style={sectionStyle('progress')} hidden={!isVisible('progress')} data-testid="bento-progress">
      <Card className="h-full" data-testid="progress-card">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Objectif mensuel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Progression</span>
              <span className="font-medium">
                {formatCurrency(stats?.total_ca || 0)} / {formatCurrency(stats?.target_ca || 0)}
              </span>
            </div>
            <Progress 
              value={Math.min(stats?.progress_percentage || 0, 100)} 
              className="h-3"
            />
            <div className="flex items-center justify-between text-sm">
              <span className={`font-medium ${
                stats?.progress_percentage >= 100 ? "text-green-600" : 
                stats?.progress_percentage >= 75 ? "text-yellow-600" : "text-muted-foreground"
              }`}>
                {stats?.progress_percentage?.toFixed(1) || 0}% atteint
              </span>
              {stats?.target_ca === 0 && (
                <span className="text-muted-foreground text-xs">
                  Aucun objectif défini pour ce mois
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      </div>

      {/* Summary Cards */}
      <div className={`${bentoOf('summary')} min-w-0`} style={sectionStyle('summary')} hidden={!isVisible('summary')} data-testid="bento-summary">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
        <Card className="h-full">
          <CardHeader>
            <CardTitle className="font-heading text-lg">
              Résumé du mois
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-muted-foreground">Total commandes</span>
                <span className="font-medium">{stats?.total_orders || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-muted-foreground">En attente</span>
                <span className="font-medium text-yellow-600">{stats?.pending_orders || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-muted-foreground">Terminées</span>
                <span className="font-medium text-green-600">{stats?.completed_orders || 0}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-muted-foreground">Stock valorisé</span>
                <span className="font-medium currency">{formatCurrency(stats?.stock_value || 0)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="h-full">
          <CardHeader>
            <CardTitle className="font-heading text-lg">
              Indicateurs clés
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-muted-foreground">CA moyen par commande</span>
                <span className="font-medium currency">
                  {formatCurrency(stats?.total_orders > 0 ? stats?.total_ca / stats?.total_orders : 0)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-muted-foreground">Taux de réalisation</span>
                <span className="font-medium">
                  {stats?.total_orders > 0 
                    ? ((stats?.completed_orders / stats?.total_orders) * 100).toFixed(1) 
                    : 0}%
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-muted-foreground">Objectif mensuel</span>
                <span className="font-medium currency">{formatCurrency(stats?.target_ca || 0)}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-muted-foreground">Écart objectif</span>
                <span className={`font-medium currency ${
                  (stats?.total_ca - stats?.target_ca) >= 0 ? "text-green-600" : "text-red-600"
                }`}>
                  {formatCurrency((stats?.total_ca || 0) - (stats?.target_ca || 0))}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      </div>

      {/* Top / Flop orders based on live profitability */}
      <div className={`${bentoOf('top-orders')} min-w-0`} style={sectionStyle('top-orders')} hidden={!isVisible('top-orders')} data-testid="bento-top-orders">
        <TopOrdersCard />
      </div>

      {/* Advanced comparison widget (per-user persisted config) */}
      <div className={`${bentoOf('advanced-compare')} min-w-0`} style={sectionStyle('advanced-compare')} hidden={!isVisible('advanced-compare')} data-testid="bento-advanced-compare">
        <DashboardCompareWidgets
          orderTypes={compareRefData.orderTypes}
          sellersList={compareRefData.sellersList}
        />
      </div>

      {/* Subcontractor compliance alerts (Phase 175) */}
      <div className={`${bentoOf('subcontractor-compliance')} min-w-0`} style={sectionStyle('subcontractor-compliance')} hidden={!isVisible('subcontractor-compliance')} data-testid="bento-subcontractor-compliance">
        <SubcontractorComplianceAlerts />
      </div>

      {/* Logistics items widget (Phase 178 Iter C) */}
      <div className={`${bentoOf('logistics-items')} min-w-0`} style={sectionStyle('logistics-items')} hidden={!isVisible('logistics-items')} data-testid="bento-logistics-items">
        <LogisticsItemsWidget />
      </div>

      {/* Materials pending lock widget (Phase 179) */}
      <div className={`${bentoOf('materials-pending-lock')} min-w-0`} style={sectionStyle('materials-pending-lock')} hidden={!isVisible('materials-pending-lock')} data-testid="bento-materials-pending-lock">
        <MaterialsPendingLockWidget />
      </div>

      </div>
      {/* end customizable sections */}

      <DashboardCustomizer
        open={customizerOpen}
        onOpenChange={setCustomizerOpen}
        sections={SECTION_DEFINITIONS}
        order={sectionOrder}
        visibility={sectionVisibility}
        onSave={saveLayout}
        onReset={resetLayout}
      />

      <PaymentReminderDialog
        open={!!reminderOrder}
        order={reminderOrder}
        onOpenChange={(v) => { if (!v) setReminderOrder(null); }}
        onSent={fetchStats}
      />
    </div>
  );
};

export default DashboardPage;
